import {
  StepAccordion,
  AccordionMinimumHeight,
  ButtonColorEnum,
  AccordionBackgroundColor,
} from "../../../../Utils/StepAccordion";
import { NorlysCheckbox } from "../../../../Utils/UI/Checkbox/NorlysCheckbox";
import { EmailInput } from "../../../../Utils/UI/EmailInput/EmailInput";
import { InputComponent } from "../../../../Utils/UI/InputComponent/InputComponent";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import { IAddressAutocompleteResult } from "../../../../../Models/AddressAutocompleteModel";
import { AddressAutocomplete } from "../../../../SharedComponents/AddressAutocomplete/AddressAutocomplete";
import "./ContactInfoStep.scss";
import Column from "../../../../Utils/Column/Column";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import { useContext, useEffect, useState } from "react";
import { MobileWrapper } from "../../../../Utils/MobileMargin/MobileMargin";
import { Box, styled } from "@mui/material";
import {
  checkConsent
} from "../../../../../Services/ConsentService";
import { useOrder } from "../../../../../Contexts/OrderContext";

interface IInputValidationResult {
  value: string;
  valid: boolean;
  error: string;
}

const ContactInfoStep = ({
  nextStep,
  previousStep,
  open,
  toggleOpen,
}: IStepInterface) => {
  const { order, getOrder, updateOrder } = useOrder();
  const partnerContentContext = useContext(PartnerContentContext);
  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const [loading, setLoading] = useState(false);	

  useEffect(() => {
    if (!showBillingAddress) {
      updateOrder({ billingAddress: "", billingAddressId: "" });
    }
  }, [showBillingAddress, updateOrder]);

  const handleEmailChange = (val: IInputValidationResult): any => {
    updateOrder({ email: val.valid ? val.value : "" });
  };

  const handlePhonenumberChange = (val: IInputValidationResult): any => {
    updateOrder({ phone: val.valid ? val.value : "" });
  };

  const formValid = () => {
    // getOrder: validAddress runs from a deferred autocomplete callback holding an older render's closure.
    const { phone, email, billingAddress } = getOrder();
    return (
      phone &&
      email &&
      ((showBillingAddress && billingAddress) || !showBillingAddress)
    );
  };

  const handleAddressChange = (val: IAddressAutocompleteResult): any => {
    if (val.valid) {
      updateOrder({
        billingAddress: val.value.tekst,
        billingAddressId: val.value.adresse.id.toString(),
      });
    } else {
      updateOrder({ billingAddress: "", billingAddressId: "" });
    }
  };

  const validAddress = (isValid: boolean): boolean => {
    if (!isValid) {
      updateOrder({ billingAddress: "", billingAddressId: "" });
      return false;
    }
    if (isValid && formValid()) {
      nextStep();
    }
    return isValid;
  };

  return (
    <StepAccordion
      loading={loading}
      loadingText={"Oplysninger tjekkes"}
      loadingTitle={"Vent venligst..."}
      label={partnerContentContext.contactInfoStep.title}
      open={open}
      click={toggleOpen}
      firstButtonLabel={
        partnerContentContext.contactInfoStep.backButton
          ? partnerContentContext.contactInfoStep.backButton
          : "Tilbage"
      }
      firstButtonOutline={true}
      firstButton={previousStep}
      secondButtonLabel={
        partnerContentContext.contactInfoStep.nextButton
          ? partnerContentContext.contactInfoStep.nextButton
          : "Næste"
      }
      secondButtonDisabled={!formValid()}
      secondButtonType={
        formValid() ? ButtonColorEnum.DARK_GREEN : ButtonColorEnum.GREY
      }
      secondButton={async () => {
		    setLoading(true);
        await checkConsent(order.phone, order.email);
		    setLoading(false);
        nextStep();
      }}
      minimumHeight={AccordionMinimumHeight.SMALL}
      firstButtonId="ContactInfoStepReturn"
      secondButtonId="ContactInfoStepForward"
      secondButtonShadow={true}
      backgroundColor={AccordionBackgroundColor.GREY}
    >
      <div className="contactInfoContainer">
        <Column>
          <EmailInput change={handleEmailChange} />
        </Column>

        <Column fullWidthMobile={true} noTooltip={true}>
          <FlexContainer>
            <InputComponent
              validationType="phone"
              placeholder={
                partnerContentContext.contactInfoStep.phonePlaceholder
                  ? partnerContentContext.contactInfoStep.phonePlaceholder
                  : "Telefonnummer"
              }
              change={handlePhonenumberChange}
            />
            
            {partnerContentContext.digitalLeadEnabled && (
              <Box sx={{ fontWeight: "bold", marginTop: "1rem" }}>
                <NorlysCheckbox
                  checked={order.digitalLead}
                  onChange={(checked) => {
                    updateOrder({ digitalLead: checked });
                  }}
                  label="Jeg vil gerne ringes op af Norlys med et tilbud om internet og TV"
                />
              </Box>
            )}
          </FlexContainer>
        </Column>

        <Column>
          <FlexContainer>
            <NorlysCheckbox
              checked={showBillingAddress}
              onChange={(checked) => {
                setShowBillingAddress(checked);
              }}
              label={partnerContentContext.contactInfoStep.billingAddressLabel}
            />
          </FlexContainer>
        </Column>

        {showBillingAddress && (
          <Column>
            <AddressAutocomplete
              validAddress={validAddress}
              placeholder="Faktureringsadresse"
              change={handleAddressChange}
            />
          </Column>
        )}

        <MobileWrapper marginTopViewheight={20}></MobileWrapper>
      </div>
    </StepAccordion>
  );
};

export default ContactInfoStep;

const FlexContainer = styled(Box)`
  display: flex;
  flex-direction: column;

  > div {
    margin-top: -0.2rem;
    margin-bottom: 1rem;
  }

  @media screen and (max-width: 768px) {
    align-items: start;

    > div {
      margin-top: -0.8rem;
      margin-bottom: 1rem;
      width: 100%;
    }
  }
`;
